import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db, googleProvider } from './firebase';
import './Login.css';

function Login() {
  const navigate = useNavigate();
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [status, setStatus] = useState("Please complete the security check.");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [appTokenChecking, setAppTokenChecking] = useState(false);
  const appTokenHandled = useRef(false);

  // --- APP HAND-OFF: mobile app opens https://.../login?token=...&source=app
  // to sign the resident straight in (no retyping credentials). The token is a
  // short-lived Firebase custom token minted by /resident-login on the app's
  // behalf. We consume it once, then scrub it from the address bar so it
  // doesn't linger in browser history.
  useEffect(() => {
    if (appTokenHandled.current) return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const source = params.get('source');

    if (!token) return;
    appTokenHandled.current = true;
    setAppTokenChecking(true);

    if (source === 'app') {
      sessionStorage.setItem('housefed_from_app', 'true');
    }

    (async () => {
      try {
        await signInWithCustomToken(auth, token);
        // Scrub the token out of the visible URL/history right away.
        window.history.replaceState({}, '', '/login');
        navigate('/resident-dashboard', { replace: true });
      } catch (err) {
        window.history.replaceState({}, '', '/login');
        setStatus("❌ Your app session has expired. Please log in below.");
        setAppTokenChecking(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const SITE_KEY = "6LeWHUEsAAAAAAs5Uimrx8GspzWH6WraM1C3GdXb";
  const BACKEND_URL = "https://api.bathindahousingcomplex.com/verify-captcha";
  const FLASK_BASE_URL = "https://api.bathindahousingcomplex.com";

  // --- TABS & RESIDENT STATES ---
  const [loginMode, setLoginMode] = useState("resident");
  const [propertyType, setPropertyType] = useState("houses");
  const [plotNo, setPlotNo] = useState("");

  // --- NEW: VENDOR/BIDDER STATES ---
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [gst, setGst] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // All hooks are declared above this point (rules-of-hooks requires every
  // hook to run on every render) - now it's safe to bail out early into the
  // "signing you in" screen for the app hand-off flow.
  if (appTokenChecking) {
    return (
      <div className="login-container">
        <div className="login-card" style={{ maxWidth: '400px', textAlign: 'center' }}>
          <h2>Signing you in…</h2>
          <p className="login-subtitle">Please wait a moment.</p>
        </div>
      </div>
    );
  }

  // --- CHECK USER ROLE & REDIRECT ---
  // --- CHECK USER ROLE & REDIRECT ---
  const handleUserRole = async (user, isGoogle = false) => {
    try {
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      
      let role = "resident"; 

      if (userSnap.exists()) {
        const userData = userSnap.data();
        role = userData.role || "resident";
        
        // --- THE FIX ---
        // If an older user logs in via the Admin/Bidder tab, upgrade their role 
        // to "bidder" so Firebase doesn't accidentally route them to the Home page.
        if (loginMode === 'standard' && role !== 'admin') {
          role = "bidder";
        }

        await setDoc(userRef, { lastLogin: new Date(), role: role }, { merge: true });
      } else {
        // If signing in via Google on the Bidder tab, assign bidder role. Otherwise resident.
        role = loginMode === 'standard' ? "bidder" : "resident";
        
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email,
          name: user.displayName || (loginMode === 'standard' ? name : "Resident"),
          role: role,
          createdAt: new Date(),
          ...(role === "bidder" ? { phoneVerified: false, phone: phone, gst: gst } : {})
        });
      }

      // REDIRECT BASED ON ROLE
      if (role === 'admin') navigate('/admin');
      else if (role === 'bidder') navigate('/bidder-dashboard');
      else navigate('/'); 

    } catch (error) {
      console.error("Role check failed:", error);
      setStatus("Error checking user profile.");
    }
  };

  const handleCaptchaChange = async (token) => {
    if (!token) return;
    setStatus("Verifying security...");
    try {
      const response = await axios.post(BACKEND_URL, { token: token });
      if (response.data.success) {
        setCaptchaVerified(true);
        setStatus("");
      } else {
        setStatus("❌ Verification failed.");
        setCaptchaVerified(false);
      }
    } catch (error) {
      setStatus("❌ Connection Error.");
    }
  };

  // --- 1. RESIDENT LOGIN ---
  const handleResidentLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus("Verifying property credentials...");

    try {
      const response = await axios.post(`${FLASK_BASE_URL}/resident-login`, {
        propertyType, plotNo, password
      });

      if (response.data.success && response.data.token) {
        await signInWithCustomToken(auth, response.data.token);
        navigate('/resident-dashboard');
      }
    } catch (error) {
      if (error.response && error.response.data) setStatus("❌ " + error.response.data.message);
      else setStatus("❌ Login failed. Server error.");
    }
    setLoading(false);
  };

  // --- 2. ADMIN/VENDOR GOOGLE LOGIN ---
  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      handleUserRole(result.user, true);
    } catch (error) { setStatus("Login Failed: " + error.message); }
  };

  // --- 3. VENDOR FORGOT PASSWORD ---
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { setStatus("❌ Please enter your email address below to reset."); return; }
    
    setLoading(true); setStatus(""); setSuccessMsg("");
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccessMsg("Password reset link sent! Check your email inbox.");
    } catch (err) {
      if(err.code === 'auth/user-not-found') setStatus("❌ No account found with this email.");
      else setStatus("❌ " + err.message);
    }
    setLoading(false);
  };

  // --- 4. VENDOR/ADMIN EMAIL LOGIN & SIGNUP ---
  // --- 4. VENDOR/ADMIN EMAIL LOGIN & SIGNUP ---
  const handleStandardSubmit = async (e) => {
    e.preventDefault();
    setStatus(""); setSuccessMsg(""); setLoading(true);
    
    try {
      // THE TRICK: If they just type "admin", secretly convert it to the full email
      let loginEmail = email.trim().toLowerCase() === "admin" ? "admin@housefed.com" : email;
      
      let result;
      if (isSignUp) {
        if (password !== confirmPassword) { setStatus("❌ Passwords do not match!"); setLoading(false); return; }
        if (phone.length < 10) { setStatus("❌ Please enter a valid phone number."); setLoading(false); return; }

        result = await createUserWithEmailAndPassword(auth, email, password);
        await handleUserRole(result.user, false);
      } else {
        result = await signInWithEmailAndPassword(auth, loginEmail, password);
        await handleUserRole(result.user, false);
      }
    } catch (err) { 
      if(err.code === 'auth/email-already-in-use') setStatus("❌ Email already registered. Please Login.");
      else if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') setStatus("❌ Invalid email or password.");
      else if (err.code === 'auth/weak-password') setStatus("❌ Password should be at least 6 characters.");
      else setStatus("❌ " + err.message);
      setLoading(false); 
    }
  };

  return (
    <div className="login-container">
      <div className="login-card" style={{ maxWidth: isSignUp && loginMode === 'standard' ? '450px' : '400px' }}>
        
        <h2>{isForgotPassword ? "Reset Password" : isSignUp && loginMode === 'standard' ? "Vendor Registration" : "Welcome Back"}</h2>
        <p className="login-subtitle">Bathinda Housing Complex</p>
        
        <div className="captcha-wrapper">
          <ReCAPTCHA sitekey={SITE_KEY} onChange={handleCaptchaChange} />
        </div>
        
        {status && <p className="status-msg">{status}</p>}
        {successMsg && <p style={{color: '#27ae60', fontWeight: 'bold', marginBottom: '15px'}}>{successMsg}</p>}

        {captchaVerified && (
          <div className="form-content fade-in">
            
            {/* TOGGLE TABS (Hidden during Signup/Forgot Pass to keep UI clean) */}
            {!isSignUp && !isForgotPassword && (
                <div className="login-tabs">
                  <button className={`tab-btn ${loginMode === 'resident' ? 'active' : ''}`} onClick={() => {setLoginMode('resident'); setStatus("");}}>
                    🏠 Resident
                  </button>
                  <button className={`tab-btn ${loginMode === 'standard' ? 'active' : ''}`} onClick={() => {setLoginMode('standard'); setStatus("");}}>
                    🏢 Admin / Bidder
                  </button>
                </div>
            )}

            {/* --- RESIDENT MODE --- */}
            {loginMode === 'resident' && (
              <form onSubmit={handleResidentLogin} className="fade-in">
                <select className="input-field" value={propertyType} onChange={(e) => setPropertyType(e.target.value)} required>
                  <option value="houses">House</option><option value="shops">Shop</option>
                  <option value="booths">Booth</option><option value="c_buildings">Commercial Building</option>
                  <option value="n_home">Nursing Home</option>
                </select>
                <input type="text" className="input-field" placeholder="Plot / Property Number" value={plotNo} onChange={(e) => setPlotNo(e.target.value)} required />
                <input type="password" className="input-field" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                <button type="submit" className="login-btn" disabled={loading}>{loading ? "Logging in..." : "Secure Login"}</button>
              </form>
            )}

            {/* --- ADMIN / BIDDER MODE --- */}
            {loginMode === 'standard' && (
              <div className="fade-in">
                
                {/* FORGOT PASSWORD VIEW */}
                {isForgotPassword ? (
                  <form onSubmit={handleForgotPassword}>
                    <input className="input-field" type="email" placeholder="Registered Email Address" value={email} onChange={(e) => setEmail(e.target.value)} required />
                    <button type="submit" className="login-btn" disabled={loading}>{loading ? "Sending..." : "Send Reset Link"}</button>
                    <button type="button" onClick={() => { setIsForgotPassword(false); setStatus(""); setSuccessMsg(""); }} style={{background:'none', border:'none', color:'#777', cursor:'pointer', marginTop:'15px', width:'100%'}}>← Back to Login</button>
                  </form>
                ) : (
                  <>
                    {/* STANDARD LOGIN/SIGNUP VIEW */}
                    {!isSignUp && (
                      <>
                        <button onClick={handleGoogleLogin} className="google-btn">Sign in with Google</button>
                        <div className="divider"><span>OR</span></div>
                      </>
                    )}

                    <form onSubmit={handleStandardSubmit}>
                      
                      {isSignUp && (
                        <>
                          <input className="input-field" type="text" placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)} required />
                          <div style={{display: 'flex', gap: '10px'}}>
                            <input className="input-field" type="tel" placeholder="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} required style={{flex: 1}} />
                            <input className="input-field" type="text" placeholder="GST / Aadhar No." value={gst} onChange={(e) => setGst(e.target.value)} required style={{flex: 1}} />
                          </div>
                        </>
                      )}

                      <input type="text" className="input-field" placeholder="Email Address / Admin" value={email} onChange={(e) => setEmail(e.target.value)} required />
                      <input type="password" className="input-field" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
                      
                      {isSignUp && (
                         <input className="input-field" type="password" placeholder="Confirm Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} />
                      )}

                      {!isSignUp && (
                         <div style={{textAlign: 'right', marginBottom: '15px'}}>
                             <button type="button" style={{background:'none', border:'none', fontSize: '13px', color: '#007bff', cursor: 'pointer', padding: 0}} onClick={() => setIsForgotPassword(true)}>Forgot Password?</button>
                         </div>
                      )}

                      <button type="submit" className="login-btn" disabled={loading}>{loading ? "Processing..." : (isSignUp ? "Complete Registration" : "Login with Email")}</button>
                    </form>

                    {/* TOGGLE SIGNUP */}
                    <button onClick={() => { setIsSignUp(!isSignUp); setStatus(""); setSuccessMsg(""); }} style={{background:'none', border:'none', color:'#777', cursor:'pointer', marginTop:'20px', width:'100%', fontWeight:'bold'}}>
                      {isSignUp ? "Already have an account? Login here" : "New Vendor? Create Account"}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;